<template>

    <div class="page">

        <!-- KtDatatable Implementation -->
        <kt-datatable
            :model="timeModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: 'table-bordered', rowClass: getRowClass }"
            :update-function="handleDatatableUpdate"
            :hide-search="true"
            storage-key="time-list"
        >
            <template #filters="scope">
                <div class="col" v-if="!props.hideFilters">
                    <div class="row">
                        <div class="col-auto">
                            <kt-autoselect
                                label="Type"
                                :options="timeTypeOptions"
                                display-by="name"
                                value-by="id"
                                v-model="currentFilter.timeType"
                                @change="setFilter({timeType: currentFilter.timeType})"
                            ></kt-autoselect>
                        </div>
                        <div class="col-auto">
                            <client-multi-check 
                                label="Clients"
                                :model-value="currentFilter.client"
                                @update:model-value="(value: any) => setFilter({client: value})"
                            ></client-multi-check>
                        </div>
                        <div class="col-auto">
                            <tech-multi-check 
                                label="Technicians"
                                :model-value="currentFilter.technician"
                                @update:model-value="(value: any) => setFilter({technician: value})"
                            ></tech-multi-check>
                        </div>
                        <div class="col-auto">
                            <kt-date-range-select
                        label="Date Range"
                        v-model="dateRange"
                    ></kt-date-range-select>
                        </div>
                    </div>
                </div>
            </template>

            <template #under-header>
                <div v-if="hasActiveFilters && !props.hideFilters" class="d-flex flex-wrap gap-2">
                    <span class="text-muted small me-2">Active filters:</span>
                    
                    <!-- Client filter badges -->
                    <span v-for="clientId in currentFilter.client" 
                          :key="`client-${clientId}`" 
                          class="badge bg-primary d-flex align-items-center">
                        Client: {{ getClientName(clientId) }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="removeClientFilter(clientId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Technician filter badges -->
                    <span v-for="techId in currentFilter.technician" 
                          :key="`tech-${techId}`" 
                          class="badge bg-success d-flex align-items-center">
                        Technician: {{ getTechName(techId) }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="removeTechFilter(techId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Date range filter badge -->
                    <span v-if="currentFilter.startDate || currentFilter.endDate" 
                          class="badge bg-info d-flex align-items-center">
                        Date: {{ currentFilter.startDate }} to {{ currentFilter.endDate }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="removeDateFilter()"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Clear all filters -->
                    <button v-if="hasActiveFilters" 
                            class="btn btn-sm btn-outline-secondary ms-2" 
                            @click="clearAllFilters()">
                      Clear All
                    </button>
                </div>
            </template>

            <template #after-search="scope">
                <div class="btn-group">
                <button @click="refresh()" 
                    class="btn btn-sm btn-outline-secondary"
                >Refresh <i class="fa fa-sync"></i></button>

                <button 
                    v-if="!props.disableActions"
                    @click="newRecord()" 
                    class="btn btn-sm btn-outline-primary"
                >New <i class="fa fa-plus"></i></button>
                </div>
            </template>

            <template #view_actions="scope">
                <kt-dropmenu v-model:visible="actionDropdown['row_' + scope.row.Id]">
                    <template #button>
                        <button
                            class="btn -btn-outline-primary btn-sm"
                            @click="actionDropdown['row_' + scope.row.Id] = !actionDropdown['row_' + scope.row.Id]"
                        >
                            <span><i class="fas fa-ellipsis-v"></i></span>
                        </button>
                    </template>
                    <template #dropmenu>
                        <ul class="dropdown-menu show">
                            <li>
                                <label class="dropdown-item">
                                    <button class="btn btn-sm btn-outline-primary form-control" @click="editRecord(scope.row.Id)">
                                        <i class="fa fa-edit"></i> Edit
                                    </button>
                                </label>
                            </li>
                            <li>
                                <label class="dropdown-item">
                                    <button class="btn btn-sm btn-outline-danger form-control" @click="deleteRecord(scope.row.Id)">
                                        <i class="fa fa-trash"></i> Remove
                                    </button>
                                </label>
                            </li>
                        </ul>
                    </template>
                </kt-dropmenu>
            </template>
            
            <template #view_string="scope">
                <span v-if="scope.column.name === 'Contact'">
                    {{ scope.row.Contact?.Code || 'N/A' }}
                </span>
                <span v-else-if="scope.column.name === 'Company'">
                    {{ scope.row.Contact?.Company || 'N/A' }}
                </span>
                <span v-else-if="scope.column.name === 'RequestId'">
                    {{ scope.row.Request?.Id || 'N/A' }}
                </span>
                <span v-else-if="scope.column.name === 'TechnicianName'">
                    {{ scope.row.Technician?.TechnicianName || 'N/A' }}
                </span>
                <span v-else-if="scope.isTruncated">
                    {{ scope.truncatedValue }}
                    <button 
                        class="btn btn-sm btn-link p-0 ms-1" 
                        @click.stop="scope.showFullText(scope.value)"
                        title="View full text"
                    >
                        <i class="fas fa-search"></i>
                    </button>
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>
            
            <template #view_number="scope">
                <span v-if="scope.column.name === 'Status'">
                    <span v-if="scope.value" :class="getStatusBadgeClass(scope.value)" class="badge">
                        <i :class="getStatusIcon(scope.value)" class="me-1"></i>
                        {{ getStatusName(scope.value) }}
                    </span>
                    <span v-else class="badge bg-light text-dark"><i class="fas fa-circle me-1"></i>No Status</span>
                </span>
                <span v-else-if="scope.column.name === 'InvoiceAmount' || scope.column.name === 'ShouldBill' || scope.column.name === 'PaidAmount'">
                    ${{ (scope.value || 0).toFixed(2) }}
                </span>
                <span v-else>
                    {{ scope.value || 0 }}
                </span>
            </template>
        </kt-datatable>
    </div>
    
    <router-view name="modal"></router-view>
    
    <!-- Embedded Edit Modal -->
    <time-edit-embedded 
        v-if="props.embedded && showEditModal" 
        :record-id="editingRecordId"
        :request-id="props.requestId"
        :client-id="props.clientId"
        :tech-id="props.techId"
        :pm-only="props.pmOnly"
        @close="closeEditModal"
    />
    
</template>

<style scoped>
/* Assignment-only rows have a light blue background */
:deep(.assignment-row) {
    background-color: #e8f4f8 !important;
}

:deep(.assignment-row:hover) {
    background-color: #d1e7f0 !important;
}

/* Row shading based on status - applied to td cells */
:deep(.row-status-completed td) {
    background-color: rgba(25, 135, 84, 0.08) !important;
}

:deep(.row-status-completed:hover td) {
    background-color: rgba(25, 135, 84, 0.15) !important;
}

:deep(.row-status-pending td) {
    background-color: rgba(255, 193, 7, 0.08) !important;
}

:deep(.row-status-pending:hover td) {
    background-color: rgba(255, 193, 7, 0.15) !important;
}

:deep(.row-status-progress td) {
    background-color: rgba(13, 110, 253, 0.08) !important;
}

:deep(.row-status-progress:hover td) {
    background-color: rgba(13, 110, 253, 0.15) !important;
}

:deep(.row-status-cancelled td) {
    background-color: rgba(220, 53, 69, 0.08) !important;
}

:deep(.row-status-cancelled:hover td) {
    background-color: rgba(220, 53, 69, 0.15) !important;
}

:deep(.row-status-scheduled td) {
    background-color: rgba(13, 202, 240, 0.08) !important;
}

:deep(.row-status-scheduled:hover td) {
    background-color: rgba(13, 202, 240, 0.15) !important;
}

:deep(.row-status-draft td) {
    background-color: rgba(108, 117, 125, 0.08) !important;
}

:deep(.row-status-draft:hover td) {
    background-color: rgba(108, 117, 125, 0.15) !important;
}
</style>
    
<script setup lang="ts">

import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter, RouterView } from "vue-router";
import Api from "@/services/ApiService";
import { ApiResponse } from "@/services/ApiService";
import { TimeModel, TimeModelFilter } from "@/models/TaskModel";
import Cache from "@/services/Cache";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import { KtDatatableModelProp } from "@/components/UI/KtDataTable";
import ClientMultiCheck from "@/components/Clients/ClientMultiCheck.vue";
import TechMultiCheck from "@/components/Techs/TechMultiCheck.vue";
import KtDateRangeSelect from "@/components/UI/KtDateRangeSelect.vue";
import KtDropmenu from "@/components/UI/KtDropmenu.vue";
import KtAutoselect from "@/components/UI/KtAutoselect.vue";
import TimeEditEmbedded from "@/components/Time/TimeEditEmbedded.vue";
import { useClientStore } from "@/stores/ClientStore";
import { useTechStore } from "@/stores/TechStore";
import { useTaskStatusStore } from "@/stores/TaskStatusStore";
import { storeToRefs } from "pinia";

const route = useRoute();
const router = useRouter();

// Props
const props = defineProps({
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    techId: {
        type: [Number, String],
        default: null
    },
    hideFilters: {
        type: Boolean,
        default: false
    },
    embedded: {
        type: Boolean,
        default: false
    },
    pmOnly: {
        type: Boolean,
        default: false
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

// Store access
const ClientStore = useClientStore();
const TechStore = useTechStore();

// Computed store lists
const ClientList = computed(() => Object.values(ClientStore.list));
const TechList = computed(() => Object.values(TechStore.list));

// Data state
const total = ref(0);
const rows = ref<TimeModel[]>([]);

// ===== STORES =====

// Task Status Store
const taskStatusStore = useTaskStatusStore();
const { list: TaskStatusList } = storeToRefs(taskStatusStore);
taskStatusStore.load();

// Time type options for the filter
const timeTypeOptions = ref([
    { id: 'All', name: 'All' },
    { id: 'Assignments', name: 'Assignments' },
    { id: 'Work', name: 'Work' }
]);

// ===== NEW CLEAN FILTER SYSTEM =====

// Filter state interface
interface TimeListFilter {
    timeType: string;
    client: number[];
    technician: number[];
    user: string;
    startDate: string;
    endDate: string;
    page: number;
    size: number;
    sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

// Current filter state (reactive)
const currentFilter = ref<TimeListFilter>({
    timeType: 'All',
    client: [],
    technician: [],
    user: '',
    startDate: '',
    endDate: '',
    page: 1,
    size: 25,
    sort: []
});

// Datatable filter for pagination (derived from currentFilter)
const datatableFilter = computed(() => ({
    page: currentFilter.value.page,
    size: currentFilter.value.size,
    total: total.value,
    search: '',
    sort: currentFilter.value.sort
}));

// Date range picker (derived from currentFilter)
const dateRange = computed({
    get: () => ({
        startDate: currentFilter.value.startDate || null,
        endDate: currentFilter.value.endDate || null
    }),
    set: (value) => {
        setFilter({
            startDate: value.startDate || '',
            endDate: value.endDate || ''
        });
    }
});

// Check if any filters are active
const hasActiveFilters = computed(() => {
    return currentFilter.value.client.length > 0 ||
           currentFilter.value.technician.length > 0 ||
           currentFilter.value.startDate !== '' ||
           currentFilter.value.endDate !== '';
});

// Legacy filter for compatibility with existing filter components
const filter = computed(() => ({
    client: currentFilter.value.client.map(c => c.toString()).join(','),
    technician: currentFilter.value.technician.map(t => t.toString()).join(','),
    user: currentFilter.value.user,
    startDate: currentFilter.value.startDate,
    endDate: currentFilter.value.endDate,
    page: currentFilter.value.page.toString()
}));

// ===== URL & FILTER UTILITIES =====

// Parse sort parameter from URL string to KtDatatable sort array format  
const parseSortFromUrl = (sortParam?: string): Array<Record<string, 'ASC' | 'DESC'>> => {
    if (!sortParam) return [];
    
    // Parse comma-separated sort string like "Id,-WorkCompleteDate,Time"
    // Where "-" prefix means DESC, no prefix means ASC
    return sortParam.split(',').map(sortField => {
        const trimmed = sortField.trim();
        if (trimmed.startsWith('-')) {
            const column = trimmed.substring(1);
            return { [column]: 'DESC' as const };
        } else {
            return { [trimmed]: 'ASC' as const };
        }
    });
};

// Parse URL params to filter object
const parseUrlToFilter = (): TimeListFilter => {
    const routeQuery = route.query;
    
    console.debug('Parsing URL params from route.query:', routeQuery);
    console.debug('Raw page param:', routeQuery.page);
    
    const parsed = {
        timeType: (routeQuery.timeType as string) || 'All',
        client: Array.isArray(routeQuery.client) 
            ? routeQuery.client.filter(c => c !== null).map(c => parseInt(c as string))
            : (routeQuery.client ? [parseInt(routeQuery.client as string)] : []),
        technician: Array.isArray(routeQuery.technician) 
            ? routeQuery.technician.filter(t => t !== null).map(t => parseInt(t as string))
            : (routeQuery.technician ? [parseInt(routeQuery.technician as string)] : []),
        user: (routeQuery.user as string) || '',
        startDate: (routeQuery.startDate as string) || '',
        endDate: (routeQuery.endDate as string) || '',
        page: parseInt((routeQuery.page as string) || '1'),
        size: parseInt((routeQuery.size as string) || '25'),
        sort: parseSortFromUrl(routeQuery.sort as string)
    };
    
    console.debug('Parsed filter:', parsed);
    return parsed;
};

// Build URL params from filter object
const buildUrlFromFilter = (filter: TimeListFilter): string => {
    const params = new URLSearchParams();
    
    // Add array parameters
    filter.client.forEach(c => c && params.append('client', c.toString()));
    filter.technician.forEach(t => t && params.append('technician', t.toString()));
    
    // Add single parameters
    if (filter.timeType !== 'All') params.set('timeType', filter.timeType);
    if (filter.user) params.set('user', filter.user);
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    
    // Add sort parameter - convert KtDatatable sort array back to URL string
    if (filter.sort && filter.sort.length > 0) {
        const sortString = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        }).join(',');
        params.set('sort', sortString);
    }
    
    // Always include page parameter (even for page 0/1)
    params.set('page', filter.page.toString());
    if (filter.size !== 25) params.set('size', filter.size.toString());
    
    return params.toString();
};

// Save filter to Cache service for persistence (consistent with other lists)
const saveFilterToStorage = (filter: TimeListFilter) => {
    try {
        Cache.set('time-list-filter', filter);
        console.debug('Filter saved to cache:', filter);
    } catch (error) {
        console.error('Failed to save filter to cache:', error);
    }
};

// Load filter from Cache service as fallback
const loadFilterFromStorage = (): TimeListFilter => {
    try {
        const stored = Cache.get('time-list-filter');
        if (stored) {
            console.debug('Loaded filter from cache:', stored);
            return stored;
        }
    } catch (error) {
        console.error('Failed to load filter from cache:', error);
    }
    
    // Return default filter if nothing in cache
    return {
        timeType: 'All',
        client: [],
        technician: [],
        user: '',
        startDate: '',
        endDate: '',
        page: 1,
        size: 25,
        sort: []
    };
};

// ===== API INTEGRATION =====

// Convert filter to API parameters
const buildApiParams = (filter: TimeListFilter) => {
    // Ensure page is at least 1 before converting to 0-based
    const page = Math.max(1, filter.page || 1);
    
    const params: any = {
        Page: page - 1,
        Size: filter.size || 25,
        IncludeRequest: true,
        IncludeContact: true
    };
    
    // If requestId prop is provided, filter by that request only
    if (props.requestId) {
        params.RequestId = props.requestId;
        // Don't apply other filters when filtering by request
        return params;
    }
    
    // Add client CustomerNumbers directly (API expects CustomerNumber, not ContactId)
    if (filter.client.length > 0) {
        params.CustomerNumber = filter.client;
    }
    
    // Add technician filter
    if (filter.technician.length > 0) params.TechId = filter.technician;
    
    // Add single filters
    if (filter.user) params.TechId = filter.user;
    if (filter.startDate) {
        params.StartDate = new Date(filter.startDate + " 00:00:00");
    }
    if (filter.endDate) {
        params.EndDate = new Date(filter.endDate + " 23:59:59");
    }
    
    // Add TimeType filter
    if (filter.timeType !== 'All') {
        params.TimeType = filter.timeType;
    }
    
    // Add OrderBy parameter for server-side sorting
    if (filter.sort && filter.sort.length > 0) {
        // Convert sort array to comma-separated OrderBy string
        // Example: [{Id: 'ASC'}] -> "Id", [{Id: 'DESC'}] -> "-Id"
        const orderByParts = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        });
        params.OrderBy = orderByParts.join(',');
    }
    
    return params;
};

// Load data from API using current filter
const loadData = async () => {
    try {
        console.debug('loadData called with currentFilter:', currentFilter.value);
        const params = buildApiParams(currentFilter.value);
        console.debug('API params:', params);
        
        const response: ApiResponse<TimeModel[]> = await Api.loadTimes(params, false);
        console.debug('API response:', response);
        
        rows.value = response.Data || [];
        total.value = response.Meta?.Total || 0;
        
        console.debug(`Loaded ${rows.value.length} time entries, total: ${total.value}`);
    } catch (error) {
        console.error('Failed to load data:', error);
        rows.value = [];
        total.value = 0;
    }
};

// ===== FILTER UPDATE HANDLERS =====

// Handle datatable pagination/sorting updates
const handleDatatableUpdate = (filterUpdate: any) => {
    console.log('Datatable filter update (pagination/sorting):', filterUpdate);
    
    // If embedded mode (requestId prop), update filter directly without routing
    if (props.requestId) {
        if (filterUpdate.page !== undefined) currentFilter.value.page = filterUpdate.page;
        if (filterUpdate.size !== undefined) currentFilter.value.size = filterUpdate.size;
        if (filterUpdate.sort !== undefined) currentFilter.value.sort = filterUpdate.sort || [];
        loadData();
        return;
    }
    
    // Create a temporary filter object for URL building (don't update currentFilter yet)
    const tempFilter = { ...currentFilter.value };
    if (filterUpdate.page !== undefined) tempFilter.page = filterUpdate.page;
    if (filterUpdate.size !== undefined) tempFilter.size = filterUpdate.size;
    if (filterUpdate.sort !== undefined) tempFilter.sort = filterUpdate.sort || [];
    
    // Build URL and navigate directly (let route watcher handle the rest)
    const urlParams = buildUrlFromFilter(tempFilter);
    const newUrl = `/time/list${urlParams ? '?' + urlParams : ''}`;
    
    console.log('Navigating to:', newUrl);
    router.replace(newUrl);
};

// Initialize filter from URL or localStorage
const initializeFilter = () => {
    console.debug('Initializing filter...');
    
    // If embedded mode (requestId prop), skip URL initialization
    if (props.requestId) {
        console.debug('Embedded mode - using default filter');
        return;
    }
    
    // Check if we have URL params
    const hasUrlParams = Object.keys(route.query).length > 0;
    
    if (hasUrlParams) {
        console.debug('Using URL params');
        currentFilter.value = parseUrlToFilter();
    } else {
        console.debug('No URL params, checking cache');
        const cachedFilter = loadFilterFromStorage();
        currentFilter.value = cachedFilter;
        
        // Update URL to reflect loaded filter
        const urlParams = buildUrlFromFilter(currentFilter.value);
        const newUrl = `/time/list${urlParams ? '?' + urlParams : ''}`;
        router.replace(newUrl);
    }
    
    console.debug('Initialized filter:', currentFilter.value);
};

// Set filter (partial update)
const setFilter = (updates: Partial<TimeListFilter>) => {
    console.debug('setFilter called with updates:', updates);
    
    // If embedded mode (requestId prop), ignore filter updates
    if (props.requestId) {
        console.debug('Embedded mode - ignoring filter updates');
        return;
    }
    
    // Merge updates into current filter
    const newFilter = { ...currentFilter.value, ...updates };
    
    // Reset to page 0 when filters change (except when page itself is being updated)
    if (!('page' in updates)) {
        newFilter.page = 0;
    }
    
    console.debug('New filter after merge:', newFilter);
    
    // Save to cache
    saveFilterToStorage(newFilter);
    
    // Build URL and navigate
    const urlParams = buildUrlFromFilter(newFilter);
    const newUrl = `/time/list${urlParams ? '?' + urlParams : ''}`;
    
    console.debug('Navigating to:', newUrl);
    router.replace(newUrl);

    loadData();
};

// ===== ROUTE WATCHER =====

// Watch route changes and reload data
watch(() => route.query, (newQuery, oldQuery) => {
    // Skip route watching in embedded mode
    if (props.requestId) return;
    
    console.debug('Route query changed:', { newQuery, oldQuery });
    
    // Parse new filter from URL
    const newFilter = parseUrlToFilter();
    
    // Check if filter actually changed (prevent infinite loops)
    const currentParams = buildUrlFromFilter(currentFilter.value);
    const newParams = buildUrlFromFilter(newFilter);
    
    if (currentParams !== newParams) {
        console.debug('Filter changed, updating and loading data');
        currentFilter.value = newFilter;
        
        // Save to cache
        saveFilterToStorage(newFilter);
        
        // Load data with new filter
        loadData();
    } else {
        console.debug('Filter unchanged, skipping reload');
    }
}, { deep: true });

// ===== HELPER FUNCTIONS =====

// Get client name by CustomerNumber
const getClientName = (customerNumber: number) => {
    const client = ClientList.value.find(c => c.CustomerNumber === customerNumber);
    return client?.Company || `Client #${customerNumber}`;
};

// Get tech name by ID
const getTechName = (techId: number) => {
    const tech = TechList.value.find(t => t.Id === techId);
    return tech?.TechnicianName || `Tech #${techId}`;
};

// Remove single client filter
const removeClientFilter = (customerNumber: number) => {
    setFilter({
        client: currentFilter.value.client.filter(c => c !== customerNumber)
    });
};

// Remove single technician filter
const removeTechFilter = (techId: number) => {
    setFilter({
        technician: currentFilter.value.technician.filter(t => t !== techId)
    });
};

// Remove date filter
const removeDateFilter = () => {
    setFilter({ startDate: '', endDate: '' });
};

const clearAllFilters = () => {
    setFilter({
        timeType: 'All',
        client: [],
        technician: [],
        startDate: '',
        endDate: '',
        sort: []
    });
};

// ===== LEGACY FUNCTION REPLACEMENTS =====

const actionDropdown = ref<Record<string, boolean>>({});

// Embedded mode state
const showEditModal = ref(false);
const editingRecordId = ref<string | null>(null);

const newRecord = () => {
    if (props.embedded) {
        editingRecordId.value = 'new';
        showEditModal.value = true;
    } else {
        router.push({
            path: "/time/new"
        });
    }
};

const refresh = () => {
    loadData();
};

const deleteRecord = (id: any) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
        // TODO: Implement delete functionality
        console.log("Delete time:", id);
    }
};

const editRecord = (id: any) => {
    if (props.embedded) {
        editingRecordId.value = id.toString();
        showEditModal.value = true;
    } else {
        router.push({
            path: "/time/edit/" + id
        });
    }
};

const closeEditModal = () => {
    showEditModal.value = false;
    editingRecordId.value = null;
    // Refresh the list after closing modal
    refresh();
};

// ===== ROW CLASS HELPER =====

// Determine if a row is an assignment-only (no work logged)
const isAssignmentOnly = (row: TimeModel) => {
    // Check if it has assignment data
    const hasAssignmentData = !!(row.WorkAssignment || row.EstimatedTime || row.CompleteByDate);
    
    // Check if it has work data
    const hasWorkData = !!(
        row.ActualTime || 
        row.ActualWork || 
        row.Status || 
        row.InvoiceAmount || 
        row.ShouldBill || 
        row.InvoiceText ||
        row.WorkCompleteDate
    );
    
    // It's assignment-only if it has assignment data but no work data
    return hasAssignmentData && !hasWorkData;
};

// Get CSS class for table row
const getRowClass = (row: TimeModel) => {
    // First check if it's an assignment-only row
    if (isAssignmentOnly(row)) {
        return 'assignment-row';
    }
    
    // Then check status for work items
    const statusId = row.Status;
    if (!statusId) return '';
    
    const status = TaskStatusList.value[statusId];
    const statusName = status?.Status?.toLowerCase() || '';
    
    if (statusName.includes('completed') || statusName.includes('done') || statusName.includes('finished') || statusName.includes('closed')) {
        return 'row-status-completed';
    } else if (statusName.includes('pending') || statusName.includes('waiting') || statusName.includes('queued')) {
        return 'row-status-pending';
    } else if (statusName.includes('progress') || statusName.includes('active') || statusName.includes('working') || statusName.includes('started')) {
        return 'row-status-progress';
    } else if (statusName.includes('cancelled') || statusName.includes('rejected') || statusName.includes('failed')) {
        return 'row-status-cancelled';
    } else if (statusName.includes('scheduled') || statusName.includes('planned') || statusName.includes('assigned')) {
        return 'row-status-scheduled';
    } else if (statusName.includes('draft') || statusName.includes('new')) {
        return 'row-status-draft';
    }
    return '';
};

// ===== STATUS HELPERS =====

// Get status name by ID
const getStatusName = (statusId: number) => {
    const status = TaskStatusList.value[statusId];
    return status?.Status || `Status #${statusId}`;
};

// Helper function to get Bootstrap badge class based on status
const getStatusBadgeClass = (statusId: number) => {
    const status = TaskStatusList.value[statusId];
    const statusName = status?.Status?.toLowerCase() || '';
    
    // Map common status names to Bootstrap badge classes
    if (statusName.includes('completed') || statusName.includes('done') || statusName.includes('finished') || statusName.includes('closed')) {
        return 'bg-success'; // Green for completed
    } else if (statusName.includes('pending') || statusName.includes('waiting') || statusName.includes('queued')) {
        return 'bg-warning'; // Yellow for pending
    } else if (statusName.includes('progress') || statusName.includes('active') || statusName.includes('working') || statusName.includes('started')) {
        return 'bg-primary'; // Blue for in progress
    } else if (statusName.includes('cancelled') || statusName.includes('rejected') || statusName.includes('failed')) {
        return 'bg-danger'; // Red for cancelled/failed
    } else if (statusName.includes('scheduled') || statusName.includes('planned') || statusName.includes('assigned')) {
        return 'bg-info'; // Light blue for scheduled
    } else if (statusName.includes('draft') || statusName.includes('new')) {
        return 'bg-secondary'; // Gray for draft/new
    } else {
        return 'bg-light text-dark'; // Light gray for unknown statuses
    }
};

// Helper function to get Font Awesome icon based on status
const getStatusIcon = (statusId: number) => {
    const status = TaskStatusList.value[statusId];
    const statusName = status?.Status?.toLowerCase() || '';
    
    if (statusName.includes('completed') || statusName.includes('done') || statusName.includes('finished') || statusName.includes('closed')) {
        return 'fas fa-check-circle';
    } else if (statusName.includes('pending') || statusName.includes('waiting') || statusName.includes('queued')) {
        return 'fas fa-clock';
    } else if (statusName.includes('progress') || statusName.includes('active') || statusName.includes('working') || statusName.includes('started')) {
        return 'fas fa-spinner fa-spin';
    } else if (statusName.includes('cancelled') || statusName.includes('rejected') || statusName.includes('failed')) {
        return 'fas fa-times-circle';
    } else if (statusName.includes('scheduled') || statusName.includes('planned') || statusName.includes('assigned')) {
        return 'fas fa-calendar-check';
    } else if (statusName.includes('draft') || statusName.includes('new')) {
        return 'fas fa-file';
    } else {
        return 'fas fa-circle';
    }
};

// ===== LIFECYCLE =====

onMounted(() => {
    console.debug('TimeList mounted, initializing...');
    
    // Stores auto-load on first access
    // Initialize filter and load data
    initializeFilter();
    loadData();
});

// ===== DATATABLE MODEL =====

// KtDatatable model definition for Time/Task list
const timeModel: Array<KtDatatableModelProp> = [
    {
        name: "Actions",
        label: "Actions",
        type: "actions",
        display: true
    },
    {
        name: "Id",
        label: "ID",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "Contact",
        label: "Client Code",
        display: true,
        type: "string"
    },
    {
        name: "Company",
        label: "Company",
        display: true,
        type: "string"
    },
    {
        name: "RequestId",
        label: "Request ID",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "TechnicianName",
        label: "Technician",
        display: true,
        type: "string"
    },
    {
        name: "WorkCompleteDate",
        label: "Work Complete",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "CompleteByDate",
        label: "Due Date",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "ActualTime",
        label: "Actual Hours",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "EstimatedTime",
        label: "Est. Hours",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "WorkAssignment",
        label: "Assignment",
        display: true,
        type: "string",
        sortable: true,
        truncateWords: 6
    },
    {
        name: "ActualWork",
        label: "Work Done",
        display: true,
        type: "string",
        truncateWords: 6
    },
    {
        name: "InvoiceTime",
        label: "Invoice Hours",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "InvoiceAmount",
        label: "Invoice Amount",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "InvoiceText",
        label: "Invoice Text",
        display: false,
        type: "string"
    },
    {
        name: "Status",
        label: "Status",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "Priority",
        label: "Priority",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "ActualOvertime",
        label: "Overtime",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "ShouldBill",
        label: "Should Bill",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "PaidAmount",
        label: "Paid Amount",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "CreateDate",
        label: "Created",
        display: false,
        type: "date",
        sortable: true
    }
];
    
</script>
